import { useContext, useEffect, useState } from 'react'
import { getStudentLevelInfo } from 'api/users/get-student-level-info'
import { Guid } from 'api/unit-assessment/types'
import { StudentWithScores } from 'contexts/assessment'
import { BookingContext } from 'contexts/booking'
import { BookingStatus, StudentLevelInfoFetchState } from 'utils/constants'

type UseStudentLevelInfoParams = {
    enabled: boolean
    students: StudentWithScores[]
    currentStudentUserId: Guid
}

const hasStudentLevelInfoFetchStarted = (
    fetchState?: StudentWithScores['studentLevelInfoFetchState']
): boolean => fetchState === StudentLevelInfoFetchState.LOADING ||
    fetchState === StudentLevelInfoFetchState.SUCCESS ||
    fetchState === StudentLevelInfoFetchState.ERROR

export const useStudentLevelInfo = ({
    enabled,
    students,
    currentStudentUserId
}: UseStudentLevelInfoParams): void => {
    const booking = useContext(BookingContext)
    const [, setFetchTick] = useState(0)

    useEffect(() => {
        if (!enabled || booking.status !== BookingStatus.VALIDATED) {
            return
        }

        const student = students.find(s => s.userId === currentStudentUserId)
        if (!student || hasStudentLevelInfoFetchStarted(student.studentLevelInfoFetchState)) {
            return
        }

        const { auth } = booking
        const studentUserId = student.userId
        student.studentLevelInfoFetchState = StudentLevelInfoFetchState.LOADING
        setFetchTick(tick => tick + 1)

        void getStudentLevelInfo(auth, studentUserId)
            .then(studentLevelInfo => {
                const targetStudent = students.find(s => s.userId === studentUserId)
                if (!targetStudent) {
                    return
                }

                targetStudent.studentLevelInfo = studentLevelInfo
                targetStudent.studentLevelInfoFetchState = StudentLevelInfoFetchState.SUCCESS
                setFetchTick(tick => tick + 1)
            })
            .catch(() => {
                const targetStudent = students.find(s => s.userId === studentUserId)
                if (!targetStudent) {
                    return
                }

                targetStudent.studentLevelInfoFetchState = StudentLevelInfoFetchState.ERROR
                setFetchTick(tick => tick + 1)
            })
    }, [booking, currentStudentUserId, enabled, students])
}
